import {
  DISPLAY_ONLY_TYPES,
  FIELD_KEY_RE,
  NOT_ALLOWED_IN_REPEATER,
  formSchemaSchema,
  type AnswerValue,
  type Condition,
  type FormData,
  type FormField,
  type FormSchema,
  type GroupCondition,
  type LeafCondition,
} from './types.js';
import { coerceNumber, evalCondition, visibleFields } from './evaluate.js';

/* ========================================================================== */
/* 1. ตรวจ schema ตอน publish                                                 */
/* ========================================================================== */

export interface SchemaIssue {
  code: string;
  path: string;
  message: string;
}

/**
 * ตรวจโครงฟอร์มก่อน publish
 *
 * เจตนา: จับความผิดพลาดที่จะทำให้ข้อมูลเสียหายภายหลัง เช่น key ซ้ำ (คำตอบทับกัน)
 * หรือ showIf ที่อ้างฟิลด์ซึ่งอยู่หลังตัวเอง (ประเมินตอนกรอกไม่ได้)
 */
export function validateSchema(input: unknown): SchemaIssue[] {
  const parsed = formSchemaSchema.safeParse(input);
  if (!parsed.success) {
    return parsed.error.issues.map((i) => ({
      code: 'FORM_SCHEMA_INVALID',
      path: i.path.join('.'),
      message: i.message,
    }));
  }

  const schema = parsed.data;
  const issues: SchemaIssue[] = [];
  const seenKeys = new Set<string>();
  const seenSections = new Set<string>();
  /** key ที่ประกาศแล้ว ณ จุดนี้ — ใช้ตรวจว่า showIf อ้างฟิลด์ก่อนหน้าเท่านั้น */
  const declared = new Set<string>();

  for (const section of schema.sections) {
    if (seenSections.has(section.key)) {
      issues.push({
        code: 'FORM_DUPLICATE_SECTION',
        path: section.key,
        message: `section key "${section.key}" ซ้ำ`,
      });
    }
    seenSections.add(section.key);

    if (section.showIf) {
      issues.push(...checkCondition(section.showIf, declared, `${section.key}.showIf`));
    }

    for (const field of section.fields) {
      const path = `${section.key}.${field.key}`;

      if (!FIELD_KEY_RE.test(field.key)) {
        issues.push({
          code: 'FORM_INVALID_KEY',
          path,
          message: `key "${field.key}" ต้องเป็น a-z, 0-9, _ ขึ้นต้นด้วยตัวอักษร ยาวไม่เกิน 50`,
        });
      }
      if (seenKeys.has(field.key)) {
        issues.push({
          code: 'FORM_DUPLICATE_KEY',
          path,
          message: `key "${field.key}" ซ้ำกับฟิลด์อื่นในฟอร์มเดียวกัน`,
        });
      }
      seenKeys.add(field.key);

      if (field.showIf) {
        issues.push(...checkCondition(field.showIf, declared, `${path}.showIf`));
      }

      issues.push(...checkFieldShape(field, path, seenKeys));

      if (!DISPLAY_ONLY_TYPES.includes(field.type)) declared.add(field.key);
      for (const child of field.fields ?? []) declared.add(`${field.key}.${child.key}`);
    }
  }

  return issues;
}

function checkFieldShape(field: FormField, path: string, seenKeys: Set<string>): SchemaIssue[] {
  const issues: SchemaIssue[] = [];

  if (field.type === 'select' || field.type === 'multiselect') {
    if (!field.options?.length && !field.optionsFrom) {
      issues.push({
        code: 'FORM_MISSING_OPTIONS',
        path,
        message: 'ฟิลด์แบบเลือกต้องมี options หรือ optionsFrom',
      });
    }
    const values = new Set<string>();
    for (const opt of field.options ?? []) {
      if (values.has(opt.value)) {
        issues.push({
          code: 'FORM_DUPLICATE_OPTION',
          path: `${path}.options`,
          message: `ค่าตัวเลือก "${opt.value}" ซ้ำ`,
        });
      }
      values.add(opt.value);
    }
  } else if (field.options?.length) {
    issues.push({
      code: 'FORM_UNEXPECTED_OPTIONS',
      path,
      message: `ฟิลด์ชนิด ${field.type} ไม่ควรมี options`,
    });
  }

  if (field.type === 'repeater') {
    if (!field.fields?.length) {
      issues.push({
        code: 'FORM_EMPTY_REPEATER',
        path,
        message: 'repeater ต้องมีฟิลด์ลูกอย่างน้อย 1 ฟิลด์',
      });
    }
    const childKeys = new Set<string>();
    for (const child of field.fields ?? []) {
      const childPath = `${path}.${child.key}`;
      if (NOT_ALLOWED_IN_REPEATER.includes(child.type)) {
        issues.push({
          code: 'FORM_NESTED_NOT_ALLOWED',
          path: childPath,
          message: `ฟิลด์ชนิด ${child.type} อยู่ใน repeater ไม่ได้`,
        });
      }
      if (childKeys.has(child.key)) {
        issues.push({
          code: 'FORM_DUPLICATE_KEY',
          path: childPath,
          message: `key ลูก "${child.key}" ซ้ำใน repeater เดียวกัน`,
        });
      }
      childKeys.add(child.key);
      if (!FIELD_KEY_RE.test(child.key)) {
        issues.push({ code: 'FORM_INVALID_KEY', path: childPath, message: 'รูปแบบ key ไม่ถูกต้อง' });
      }
      issues.push(...checkFieldShape(child as FormField, childPath, seenKeys));
    }
  } else if (field.fields?.length) {
    issues.push({
      code: 'FORM_UNEXPECTED_CHILDREN',
      path,
      message: `ฟิลด์ชนิด ${field.type} ไม่ควรมีฟิลด์ลูก`,
    });
  }

  if (field.min !== undefined && field.max !== undefined && field.min > field.max) {
    issues.push({ code: 'FORM_BAD_RANGE', path, message: 'min มากกว่า max' });
  }

  return issues;
}

function isGroup(c: Condition): c is GroupCondition {
  return 'all' in c || 'any' in c;
}

function checkCondition(condition: Condition, declared: Set<string>, path: string): SchemaIssue[] {
  if (isGroup(condition)) {
    const children = condition.all ?? condition.any ?? [];
    return children.flatMap((c, i) => checkCondition(c, declared, `${path}[${i}]`));
  }

  const leaf = condition as LeafCondition;
  const ops = ['eq', 'neq', 'in', 'nin', 'gt', 'gte', 'lt', 'lte', 'truthy'] as const;
  const used = ops.filter((op) => leaf[op] !== undefined);

  const issues: SchemaIssue[] = [];
  if (used.length === 0) {
    issues.push({ code: 'FORM_BAD_CONDITION', path, message: 'เงื่อนไขไม่มีตัวดำเนินการ' });
  }
  if (!declared.has(leaf.field)) {
    issues.push({
      code: 'FORM_BAD_CONDITION',
      path,
      message: `เงื่อนไขอ้างฟิลด์ "${leaf.field}" ที่ยังไม่ถูกประกาศก่อนหน้านี้`,
    });
  }
  return issues;
}

/** ตรวจว่า scoring_json อ้างเฉพาะฟิลด์ที่มีจริง */
export function validateScoring(scoring: unknown, schema: FormSchema): SchemaIssue[] {
  if (scoring === null || scoring === undefined) return [];

  const obj = scoring as { rules?: Array<{ field?: string }> };
  if (!Array.isArray(obj.rules)) return [];

  const keys = new Set<string>();
  for (const s of schema.sections) for (const f of s.fields) keys.add(f.key);

  return obj.rules
    .filter((r) => r.field && !keys.has(r.field))
    .map((r) => ({
      code: 'FORM_BAD_SCORING',
      path: `scoring.${r.field}`,
      message: `สูตรคะแนนอ้างฟิลด์ "${r.field}" ที่ไม่มีในฟอร์ม`,
    }));
}

/* ========================================================================== */
/* 2. ตรวจคำตอบตอน submit                                                     */
/* ========================================================================== */

export interface AnswerIssue {
  field: string;
  message: string;
}

/**
 * ตรวจคำตอบเทียบกับ schema
 *
 * ต้องเรียกที่ฝั่ง Worker เสมอ ไม่เชื่อว่า client ตรวจมาแล้ว — payload แก้ได้
 * ฟิลด์ที่ถูกซ่อนด้วย showIf จะไม่ถูกบังคับ required และค่าที่ส่งมาจะถูกตัดทิ้ง
 */
export function validateAnswers(
  schema: FormSchema,
  data: FormData,
  opts: { hideSensitive?: boolean } = {},
): { issues: AnswerIssue[]; cleaned: FormData } {
  const issues: AnswerIssue[] = [];
  const cleaned: FormData = {};

  for (const { field } of visibleFields(schema, data, opts.hideSensitive ?? false)) {
    const raw = data[field.key];
    const result = checkValue(field, raw);

    if (result.issue) {
      issues.push({ field: field.key, message: result.issue });
      continue;
    }
    if (result.value !== undefined) cleaned[field.key] = result.value;
  }

  return { issues, cleaned };
}

function isEmpty(v: unknown): boolean {
  return (
    v === undefined ||
    v === null ||
    v === '' ||
    (Array.isArray(v) && v.length === 0)
  );
}

function checkValue(
  field: FormField,
  raw: unknown,
): { value?: AnswerValue; issue?: string } {
  if (isEmpty(raw)) {
    if (field.required) return { issue: `${field.label}: จำเป็นต้องกรอก` };
    // photo/repeater ที่กำหนด min >= 1 ถือว่าบังคับโดยปริยาย แม้ไม่ได้ตั้ง required
    // (ไม่งั้น "ต้องมีอย่างน้อย 1 รูป" จะถูกข้ามเมื่อผู้ใช้ไม่ส่งรูปมาเลย)
    if ((field.type === 'photo' || field.type === 'repeater') && (field.min ?? 0) >= 1) {
      return {
        issue:
          field.type === 'photo'
            ? `${field.label}: ต้องมีอย่างน้อย ${field.min} รูป`
            : `${field.label}: ต้องมีอย่างน้อย ${field.min} รายการ`,
      };
    }
    return {};
  }

  switch (field.type) {
    case 'text':
    case 'textarea': {
      const s = String(raw);
      if (field.maxLength && s.length > field.maxLength) {
        return { issue: `${field.label}: ยาวเกิน ${field.maxLength} ตัวอักษร` };
      }
      return { value: s };
    }

    case 'number':
    case 'currency':
    case 'rating': {
      const n = coerceNumber(raw);
      if (n === null) return { issue: `${field.label}: ต้องเป็นตัวเลข` };
      if (field.type === 'rating' && (n < 1 || n > 5)) {
        return { issue: `${field.label}: ต้องอยู่ระหว่าง 1-5` };
      }
      if (field.min !== undefined && n < field.min) {
        return { issue: `${field.label}: ต้องไม่น้อยกว่า ${field.min}` };
      }
      if (field.max !== undefined && n > field.max) {
        return { issue: `${field.label}: ต้องไม่เกิน ${field.max}` };
      }
      if (field.type === 'currency' && !Number.isInteger(n)) {
        return { issue: `${field.label}: จำนวนเงินต้องเป็นสตางค์ (จำนวนเต็ม)` };
      }
      return { value: n };
    }

    case 'date': {
      const s = String(raw);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(s) || Number.isNaN(Date.parse(s))) {
        return { issue: `${field.label}: รูปแบบวันที่ต้องเป็น YYYY-MM-DD` };
      }
      return { value: s };
    }

    case 'boolean': {
      if (typeof raw === 'boolean') return { value: raw };
      if (raw === 0 || raw === 1 || raw === '0' || raw === '1') return { value: Boolean(Number(raw)) };
      if (raw === 'true' || raw === 'false') return { value: raw === 'true' };
      return { issue: `${field.label}: ต้องเป็นใช่หรือไม่ใช่` };
    }

    case 'select': {
      const s = String(raw);
      if (field.options?.length && !field.options.some((o) => o.value === s)) {
        return { issue: `${field.label}: ค่า "${s}" ไม่อยู่ในตัวเลือก` };
      }
      return { value: s };
    }

    case 'multiselect': {
      if (!Array.isArray(raw)) return { issue: `${field.label}: ต้องเป็นรายการ` };
      const values = raw.map(String);
      if (field.options?.length) {
        const allowed = new Set(field.options.map((o) => o.value));
        const bad = values.filter((v) => !allowed.has(v));
        if (bad.length) {
          return { issue: `${field.label}: ค่า "${bad.join(', ')}" ไม่อยู่ในตัวเลือก` };
        }
      }
      if (field.max !== undefined && values.length > field.max) {
        return { issue: `${field.label}: เลือกได้ไม่เกิน ${field.max} ข้อ` };
      }
      return { value: values };
    }

    case 'photo': {
      if (!Array.isArray(raw)) return { issue: `${field.label}: ต้องเป็นรายการรูป` };
      const keys = raw.map(String).filter(Boolean);
      const min = field.min ?? (field.required ? 1 : 0);
      if (keys.length < min) return { issue: `${field.label}: ต้องมีอย่างน้อย ${min} รูป` };
      if (field.max !== undefined && keys.length > field.max) {
        return { issue: `${field.label}: ได้ไม่เกิน ${field.max} รูป` };
      }
      return { value: keys };
    }

    case 'signature':
      return { value: String(raw) };

    case 'gps': {
      const p = raw as { lat?: unknown; lng?: unknown; accuracy?: unknown };
      const lat = coerceNumber(p?.lat);
      const lng = coerceNumber(p?.lng);
      if (lat === null || lng === null || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return { issue: `${field.label}: พิกัดไม่ถูกต้อง` };
      }
      const acc = coerceNumber(p?.accuracy);
      return { value: acc === null ? { lat, lng } : { lat, lng, accuracy: acc } };
    }

    case 'repeater': {
      if (!Array.isArray(raw)) return { issue: `${field.label}: ต้องเป็นรายการ` };
      if (field.max !== undefined && raw.length > field.max) {
        return { issue: `${field.label}: เพิ่มได้ไม่เกิน ${field.max} รายการ` };
      }
      if (field.min !== undefined && raw.length < field.min) {
        return { issue: `${field.label}: ต้องมีอย่างน้อย ${field.min} รายการ` };
      }

      const rows: Array<Record<string, string | number | boolean | null>> = [];
      for (let i = 0; i < raw.length; i++) {
        const row = raw[i] as Record<string, unknown>;
        const out: Record<string, string | number | boolean | null> = {};
        for (const child of field.fields ?? []) {
          // เงื่อนไขภายในแถวประเมินจากค่าในแถวเดียวกัน
          if (!evalCondition(child.showIf, row as FormData)) continue;
          const r = checkValue(child as FormField, row?.[child.key]);
          if (r.issue) return { issue: `${field.label} แถวที่ ${i + 1} — ${r.issue}` };
          if (r.value !== undefined) {
            out[child.key] = r.value as string | number | boolean | null;
          }
        }
        rows.push(out);
      }
      return { value: rows };
    }

    case 'note':
      return {};
  }
}
